/** SUNTRONIX ONE — Tech Library.
 *
 *  Route: /technology/tech-library
 *  Purpose: One searchable technical document library over the
 *  existing Product Master.  No duplicate catalogue.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DocReviewQueue from "@/components/product/DocReviewQueue";
import {
  BookOpen, Upload, Plus, Search as SearchIcon, FileText, Image,
  ShieldCheck, Lock, Download, ChevronRight, Package, Filter,
  History, RefreshCw,
} from "lucide-react";

/* ---- constants shared with the modal ---- */
export const DOC_TYPES = [
  ["TECHNICAL_DATA_SHEET",    "Technical Data Sheet"],
  ["TECHNICAL_SPECIFICATION", "Technical Specification"],
  ["PRODUCT_BROCHURE",       "Product Brochure"],
  ["USER_MANUAL",                       "User Manual"],
  ["INSTALLATION_GUIDE",         "Installation Guide"],
  ["COMPATIBILITY_GUIDE",       "Compatibility Guide"],
  ["WARRANTY_DOCUMENT",       "Warranty Document"],
  ["OEM_CERTIFICATE",                "OEM Certificate"],
  ["PRODUCT_IMAGE",                    "Product Image"],
  ["COMPARISON_SHEET",            "Comparison Sheet"],
  ["OTHER_PRODUCT_DOCUMENT", "Other Product Document"],
  ["INTERNAL_PRODUCT_NOTE",    "Internal Product Note"],
];

const fmtSize = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtWhen = (v) => v ? new Date(v).toLocaleDateString(undefined,
  { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function TechLibrary() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState("");
  const [visibility, setVisibility] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [showUpload, setShowUpload] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (docType) p.set("doc_type", docType);
    if (visibility) p.set("visibility", visibility);
    if (status) p.set("status", status);
    p.set("limit", "50");
    try {
      const { data } = await api.get(`/tech-library/documents?${p}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  }, [q, docType, visibility, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="tech-library-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
            Technology · Tech Library
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-0.5">
            Technical documents
          </h1>
          <div className="text-[13px] text-slate-600 mt-1 max-w-2xl">
            One searchable home for datasheets, brochures, manuals and OEM
            certificates linked to your Product Master.  Documents default to
            <span className="font-semibold"> INTERNAL ONLY</span> until an
            authorised user marks them customer-safe.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading}
            className="h-9" data-testid="tl-refresh">
            <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (loading ? "animate-spin" : "")} />
            Refresh
          </Button>
          <Button onClick={() => setShowUpload(true)}
            data-testid="tl-add-btn"
            className="h-9 bg-emerald-700 hover:bg-emerald-800 text-white">
            <Upload className="h-3.5 w-3.5 mr-1" />
            Add tech document
          </Button>
        </div>
      </div>

      {/* Review queue for externally imported docs */}
      <DocReviewQueue onChanged={load} />

      {/* Filters */}
      <div className="border border-slate-200 rounded-md bg-white p-3 flex flex-wrap items-center gap-2"
        data-testid="tl-filters">
        <div className="flex items-center gap-2 h-9 px-2.5 border border-slate-200 rounded-md focus-within:border-emerald-400 flex-1 min-w-[260px]">
          <SearchIcon className="h-3.5 w-3.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, product, brand, part number…"
            data-testid="tl-search"
            className="flex-1 outline-none text-[13px]" />
        </div>
        <select value={docType} onChange={(e) => setDocType(e.target.value)}
          data-testid="tl-filter-type"
          className="h-9 px-2 border border-slate-200 rounded-md text-[12px]">
          <option value="">All document types</option>
          {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
          data-testid="tl-filter-visibility"
          className="h-9 px-2 border border-slate-200 rounded-md text-[12px]">
          <option value="">Any visibility</option>
          <option value="INTERNAL_ONLY">Internal only</option>
          <option value="CUSTOMER_SAFE">Customer safe</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          data-testid="tl-filter-status"
          className="h-9 px-2 border border-slate-200 rounded-md text-[12px]">
          <option value="ACTIVE">Active</option>
          <option value="SUPERSEDED">Superseded</option>
          <option value="INACTIVE">Inactive</option>
          <option value="">All statuses</option>
        </select>
      </div>

      {/* Results table */}
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[12px] text-slate-600">
            <span className="font-semibold text-slate-900 tabular-nums">{total}</span> document{total === 1 ? "" : "s"}
          </div>
        </div>
        {items.length === 0 && !loading && (
          <div className="p-8 text-center text-[13px] text-slate-500">
            No documents yet.  Click <span className="font-medium">Add tech document</span> to upload the first one.
          </div>
        )}
        <ul className="divide-y divide-slate-100" data-testid="tl-list">
          {items.map((d) => (
            <li key={d.id}
              data-testid={`tl-row-${d.id}`}
              className="grid grid-cols-[36px_1fr_180px_120px_100px_36px] items-center gap-3 px-3 py-2 hover:bg-slate-50">
              <FileGlyph mime={d.file_type} kind={d.doc_type} />
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-900 truncate">
                  {d.title}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {d.product_name || d.brand || "unlinked"} · v{d.version || "1.0"} · {d.language || "en"} · {fmtSize(d.size)}
                </div>
              </div>
              <div className="text-[11px] text-slate-600 truncate">
                {DOC_TYPES.find(([v]) => v === d.doc_type)?.[1] || d.doc_type}
              </div>
              <VisibilityBadge v={d.visibility} />
              <StatusBadge s={d.status} />
              <div className="flex items-center gap-1">
                <button onClick={() => setPreviewing(d)}
                  data-testid={`tl-preview-${d.id}`}
                  title="Preview"
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-600">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {showUpload && (
        <UploadModal onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); load(); }} />
      )}
      {previewing && (
        <PreviewDrawer doc={previewing}
          onClose={() => setPreviewing(null)}
          onChanged={() => { load(); setPreviewing(null); }} />
      )}
    </div>
  );
}

/* ================================================================ */
function FileGlyph({ mime, kind }) {
  const isImg = (mime || "").startsWith("image/") || kind === "PRODUCT_IMAGE";
  return (
    <div className={"h-9 w-9 grid place-items-center rounded " +
      (isImg ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700")}>
      {isImg ? <Image className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
    </div>
  );
}

function VisibilityBadge({ v }) {
  const safe = v === "CUSTOMER_SAFE";
  return (
    <span className={
      "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium " +
      (safe ? "text-emerald-700 border-emerald-200 bg-emerald-50"
              : "text-slate-700 border-slate-200 bg-slate-50")
    }>
      {safe ? <ShieldCheck className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
      {safe ? "customer safe" : "internal only"}
    </span>
  );
}

function StatusBadge({ s }) {
  const cls = s === "ACTIVE"      ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                    : s === "SUPERSEDED" ? "text-amber-800 border-amber-200 bg-amber-50"
                    :                                   "text-slate-500 border-slate-200 bg-slate-50";
  return (
    <span className={"inline-flex text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium " + cls}>
      {(s || "").toLowerCase()}
    </span>
  );
}

/* ================================================================
 *  UploadModal — mandatory Title / Type / File; optional Product,
 *  Version, Visibility, Description, Dates, Supersedes.
 * ================================================================ */
export function UploadModal({ onClose, onSaved, preselectProduct }) {
  const [title,        setTitle]        = useState("");
  const [docType,   setDocType]   = useState("TECHNICAL_DATA_SHEET");
  const [product,   setProduct]     = useState(preselectProduct || null);
  const [prodQ,      setProdQ]        = useState("");
  const [prodHits, setProdHits]    = useState([]);
  const [prodOpen, setProdOpen] = useState(false);
  const [brand,       setBrand]        = useState("");
  const [visibility, setVisibility]  = useState("INTERNAL_ONLY");
  const [version,   setVersion]      = useState("1.0");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [supersedes, setSupersedes] = useState(null);

  useEffect(() => {
    if (!prodOpen) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/product-master/products?page_size=8${prodQ ? `&q=${encodeURIComponent(prodQ)}` : ""}`);
        setProdHits(data.items || []);
      } catch { setProdHits([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [prodQ, prodOpen]);

  const save = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("doc_type", docType);
      fd.append("visibility", visibility);
      if (product?.id) fd.append("product_id", product.id);
      if (brand)                 fd.append("brand", brand);
      if (version)              fd.append("version", version);
      if (description)      fd.append("description", description);
      if (supersedes?.id) fd.append("supersedes_id", supersedes.id);
      fd.append("file", file);
      const { data } = await api.post("/tech-library/documents", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.title}`);
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4"
      onClick={onClose} data-testid="tl-upload-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto shadow-xl">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Tech Library
            </div>
            <h3 className="text-lg font-heading font-semibold">Add tech document</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>

        <div className="p-4 space-y-3">
          <FieldGroup label="Title *">
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="tl-up-title"
              placeholder="e.g. MacBook Pro M4 Datasheet — 14-inch — 2025 rev." />
          </FieldGroup>

          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Document type *">
              <select value={docType} onChange={(e) => setDocType(e.target.value)}
                data-testid="tl-up-type"
                className="w-full h-9 px-2 border border-slate-200 rounded-md text-[12px]">
                {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </FieldGroup>
            <FieldGroup label="Version">
              <Input value={version} onChange={(e) => setVersion(e.target.value)}
                placeholder="1.0" data-testid="tl-up-version" />
            </FieldGroup>
          </div>

          <FieldGroup label="Related product">
            <div className="relative">
              {product ? (
                <div className="flex items-center gap-2 h-9 px-2.5 border border-emerald-300 bg-emerald-50/50 rounded-md">
                  <Package className="h-3.5 w-3.5 text-emerald-700" />
                  <div className="flex-1 min-w-0 text-[12px] truncate">
                    <span className="font-medium">{product.product_name}</span>
                    <span className="text-slate-500 ml-1">
                      {product.product_code || product.sku || ""}
                    </span>
                  </div>
                  <button onClick={() => setProduct(null)}
                    className="text-[11px] text-rose-700"
                    data-testid="tl-up-clear-product">
                    Clear
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setProdOpen(true)}
                  data-testid="tl-up-product-open"
                  className="w-full h-9 px-2.5 border border-slate-200 hover:border-emerald-300 rounded-md text-left text-[12px] text-slate-400 flex items-center gap-2">
                  <Package className="h-3.5 w-3.5 text-slate-400" />
                  Link to a Product Master row (optional)
                </button>
              )}
              {prodOpen && (
                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-slate-100">
                    <input autoFocus value={prodQ}
                      onChange={(e) => setProdQ(e.target.value)}
                      placeholder="Search product…"
                      data-testid="tl-up-product-search"
                      className="w-full h-8 px-2 outline-none text-[12px] border border-slate-200 rounded" />
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {prodHits.map((p) => (
                      <button key={p.id}
                        onClick={() => {
                          setProduct(p);
                          if (!brand && p.brand) setBrand(p.brand);
                          setProdOpen(false); setProdQ("");
                        }}
                        data-testid={`tl-up-hit-${p.id}`}
                        className="w-full text-left px-3 py-1.5 hover:bg-emerald-50 text-[12px] border-b border-slate-100 last:border-b-0">
                        <div className="font-medium truncate">{p.product_name}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {p.product_code || p.sku || "—"} · {p.brand || "—"}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-slate-100">
                    <button className="w-full px-3 py-2 text-[12px] text-slate-500 hover:bg-slate-50"
                      onClick={() => setProdOpen(false)}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </FieldGroup>

          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Brand / manufacturer">
              <Input value={brand} onChange={(e) => setBrand(e.target.value)}
                placeholder="Auto-populated from Product"
                data-testid="tl-up-brand" />
            </FieldGroup>
            <FieldGroup label="Visibility">
              <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
                data-testid="tl-up-visibility"
                className="w-full h-9 px-2 border border-slate-200 rounded-md text-[12px]">
                <option value="INTERNAL_ONLY">Internal only (default)</option>
                <option value="CUSTOMER_SAFE">Customer safe</option>
              </select>
            </FieldGroup>
          </div>

          <FieldGroup label="Description">
            <textarea rows={2} value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="tl-up-desc"
              placeholder="Optional summary of what this document covers"
              className="w-full p-2 border border-slate-200 rounded-md text-[12px]" />
          </FieldGroup>

          <FieldGroup label="File *">
            <div className="border-2 border-dashed border-slate-200 rounded-md p-4 text-center hover:border-emerald-300"
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] || null); }}>
              {file ? (
                <div className="text-[12px]">
                  <div className="font-medium">{file.name}</div>
                  <div className="text-slate-500 text-[11px]">{fmtSize(file.size)}</div>
                  <button className="text-[11px] text-rose-700 mt-1"
                    onClick={() => setFile(null)}>Choose different file</button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="h-5 w-5 text-slate-400 mx-auto" />
                  <div className="text-[12px] text-slate-600 mt-1">
                    Drop file or <span className="text-emerald-700 underline">browse</span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    PDF · DOCX · XLSX · PPTX · Images · TXT · CSV — up to 40 MB
                  </div>
                  <input type="file" className="hidden"
                    data-testid="tl-up-file"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
              )}
            </div>
          </FieldGroup>
        </div>

        <div className="p-4 border-t border-slate-100 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={save} disabled={busy}
            data-testid="tl-up-save"
            className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white">
            {busy ? "Uploading…" : "Upload document"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 *  PreviewDrawer — right-side details with download / open,
 *  version history, and inline PDF/image preview.
 * ================================================================ */
function PreviewDrawer({ doc, onClose, onChanged }) {
  const [full, setFull] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tech-library/documents/${doc.id}`);
        setFull(data);
      } catch { setFull(doc); }
    })();
  }, [doc]);

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/tech-library/documents/${doc.id}`, body);
      toast.success("Updated");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const previewSrc = `${(api.defaults?.baseURL || "")}/tech-library/documents/${doc.id}/file`;
  const isPdf = (doc.file_type || "").includes("pdf");
  const isImg = (doc.file_type || "").startsWith("image/");
  const rec = full || doc;

  return (
    <div className="fixed inset-0 z-[70] bg-black/30" onClick={onClose}
      data-testid="tl-preview">
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-3xl bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-100 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {DOC_TYPES.find(([v]) => v === rec.doc_type)?.[1] || rec.doc_type}
            </div>
            <h3 className="text-lg font-heading font-semibold truncate">
              {rec.title}
            </h3>
            <div className="text-[11px] text-slate-500 mt-0.5">
              v{rec.version} · {fmtSize(rec.size)} · {rec.language} · uploaded {fmtWhen(rec.created_at)} by {rec.uploader_name}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]"
            data-testid="tl-preview-close">Close ✕</button>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_240px] gap-4">
          <div>
            {isPdf ? (
              <iframe src={previewSrc} title="doc"
                className="w-full h-[560px] border border-slate-200 rounded"
                data-testid="tl-preview-frame" />
            ) : isImg ? (
              <img src={previewSrc} alt={rec.title}
                className="max-w-full max-h-[560px] border border-slate-200 rounded"
                data-testid="tl-preview-img" />
            ) : (
              <div className="border border-slate-200 rounded p-8 text-center text-[12px] text-slate-500">
                Inline preview isn’t supported for this file type.
                <div className="mt-2">
                  <a href={previewSrc} download
                    className="text-emerald-700 underline">Download to view</a>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <MetaRow label="Status" value={<StatusBadge s={rec.status} />} />
            <MetaRow label="Visibility" value={<VisibilityBadge v={rec.visibility} />} />
            <MetaRow label="Product" value={rec.product_name || "—"} />
            <MetaRow label="Brand"    value={rec.brand || "—"} />
            <MetaRow label="Part No."  value={rec.oem_part_number || rec.sku || "—"} />
            <MetaRow label="Category" value={rec.category || "—"} />

            {/* Actions */}
            <div className="pt-2 space-y-1.5">
              <a href={previewSrc} download
                data-testid="tl-download"
                className="w-full h-9 border border-slate-200 hover:border-emerald-300 rounded-md flex items-center justify-center gap-1.5 text-[12px] text-slate-700">
                <Download className="h-3.5 w-3.5" /> Download
              </a>
              {rec.visibility === "INTERNAL_ONLY" && (
                <Button size="sm" variant="outline" disabled={saving}
                  data-testid="tl-mark-safe"
                  onClick={() => patch({ visibility: "CUSTOMER_SAFE" })}
                  className="w-full">
                  Mark customer safe
                </Button>
              )}
              {rec.visibility === "CUSTOMER_SAFE" && (
                <Button size="sm" variant="outline" disabled={saving}
                  onClick={() => patch({ visibility: "INTERNAL_ONLY" })}
                  className="w-full">
                  Revert to internal only
                </Button>
              )}
              {rec.status === "ACTIVE" && (
                <Button size="sm" variant="outline" disabled={saving}
                  onClick={() => patch({ status: "INACTIVE" })}
                  className="w-full">
                  Deactivate
                </Button>
              )}
              {rec.status !== "ACTIVE" && (
                <Button size="sm" variant="outline" disabled={saving}
                  onClick={() => patch({ status: "ACTIVE" })}
                  className="w-full">
                  Mark active
                </Button>
              )}
            </div>

            {(rec.previous_versions || []).length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1">
                  <History className="h-3 w-3" /> Previous versions
                </div>
                <ul className="text-[11px] space-y-1">
                  {rec.previous_versions.map((p) => (
                    <li key={p.id} className="flex items-center justify-between">
                      <span>v{p.version} — {fmtWhen(p.created_at)}</span>
                      <span className="text-slate-400">SUPERSEDED</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[12px] text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
